import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
    TreeView,
    processTreeViewItems,
    handleTreeViewCheckChange,
} from '@progress/kendo-react-treeview'
import '@progress/kendo-react-animation'
import { getAPIURL, getBaseURL } from '../../GlobalComponents/URLs'
import { Input } from '@progress/kendo-react-inputs'
import {
    custTreeViewAPIDataAdded,
    custTreeViewCheckChanged,
    custTreeViewExpandChanged,
    custTreeViewFilterChanged,
} from '../../features/customerTreeView/customerTreeViewSlice'

export default function CustomerSiteTreeView({ onCustTVCheck }) {
    const dispatch = useDispatch()
    const dataState = useSelector((state) => state.customerTreeViewReducer)

    function getTextWidth(text, font) {
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        context.font = font || getComputedStyle(document.body).font
        return context.measureText(text).width
    }

    // Call API to get Customer Entities and Sites
    React.useEffect(() => {
        function convertAPIData(apiData, level) {
            let text = ''
            let value = ''
            let items = ''
            let set = []
            for (var i in apiData) {
                for (var field in apiData[i]) {
                    switch (field) {
                    case 'name':
                        text = apiData[i].name
                        break
                    case 'id':
                        value = level.toString() + '|' + apiData[i].id
                        break
                    case 'children':
                        items = convertAPIData(apiData[i].children, level + 1)
                        break
                    default:
                    }
                }
                if (items === '') {
                    if (level === 0) {
                        set.push({ text: text, value: value, expanded: true })
                    } else {
                        set.push({ text: text, value: value })
                    }
                } else {
                    if (level === 0) {
                        set.push({
                            text: text,
                            value: value,
                            expanded: true,
                            items: items,
                        })
                    } else {
                        set.push({ text: text, value: value, items: items })
                    }
                }
            }
            return set
        }
        if (dataState.lastDataRetrieval === null) {
            let tree_url = getAPIURL() + 'lcm/customersitetree'
            fetch(tree_url, { credentials: 'include' })
                .then((resp) => {
                    if (resp.ok) {
                        return resp.json()
                    } else throw new Error('bad request ' + resp.statusText)
                })
                .then((json) => {
                    let newNodes = convertAPIData(json, 0)
                    let entityTagCount = 0
                    let totalTagCount = 0
                    let entityCount = 0
                    let ids = []
                    let expandableIds = []
                    let entityMaxPixels = 200 // default
                    newNodes.forEach((item) => {
                        ids.push(item.value)
                        item.items.forEach((entity) => {
                            ids.push(entity.value)
                            entity.items.forEach((site) => {
                                ids.push(site.value)
                                let sWork = site.text
                                let j = sWork.lastIndexOf('(')
                                sWork = sWork.substring(j + 1).replace(')', '')
                                entityTagCount += parseInt(sWork)
                            })
                            entity.text = entity.text + ' (' + entityTagCount.toString() + ')'

                            // Get length of longest entity name in pixels to set width of treeview (default to Body fonts)
                            let textWidth = getTextWidth(entity.text)
                            if (textWidth > entityMaxPixels) entityMaxPixels = textWidth

                            expandableIds.push(entity.text)
                            totalTagCount += entityTagCount
                            entityTagCount = 0
                            entityCount += 1
                        })
                        item.text = item.text + ' (' + totalTagCount.toString() + ')'
                        entityTagCount = 0
                    })
                    // Check entire tree at initialization only if 100 entities or fewer
                    if (entityCount > 100) ids = []
                    let newCheck = {
                        ids: ids,
                        idField: 'value',
                        applyCheckIndeterminate: true,
                    }
                    let newTv = {
                        nodes: newNodes,
                        check: newCheck,
                        entityCount: entityCount,
                        entityMaxPixels: Math.trunc(entityMaxPixels),
                        lastDataRetrieval: new Date(),
                    }
                    dispatch(custTreeViewAPIDataAdded(newTv))
                    if (entityCount < 101) onCustTVCheck(newCheck) // Check all on init if 100 or fewer entities

                    // Expand entire tree at initialization only if 100 entities or fewer
                    if (entityCount < 101)
                        dispatch(custTreeViewExpandChanged({ ids: expandableIds, idField: 'text' }))
                })
                .catch((e) => {
                    //alert('catch: ' + e.message)
                    // Redirect to Login Page
                    let redirectURL = getAPIURL().replace('/webapi/', '') + '/logon.aspx'
                    if (redirectURL.toUpperCase().includes('NET'))
                        redirectURL =
                            getAPIURL().replace('/webapi/', '') + '/masterclient/main.aspx'
                    let originalURL = window.location.href
                    window.location.href =
                        redirectURL + '?Redirect=' + encodeURIComponent(originalURL)
                })
        }
    }, [dataState.lastDataRetrieval, onCustTVCheck, dispatch])

    function onExpandChange(event) {
        let ids = dataState.expand.ids.slice()
        const index = ids.indexOf(event.item.text)
        index === -1 ? ids.push(event.item.text) : ids.splice(index, 1)
        dispatch(custTreeViewExpandChanged({ ids: ids, idField: 'text' }))
    }

    function onCheckChange(event) {
        if (!dataState.listViewLoading) {
            // // check if clearing all and optimize
            // if (event.item.value === "0|-1" && dataState.filter !== "") {
            //     alert("Global selection is not available with filter use!");
            // }
            // else {
            let maxEntities = 10000
            // Don't allow manual selection of All Entities if more than maxEntities
            if (dataState.entityCount > maxEntities && event.item.text.includes('Your Sites:')) {
                alert('Cannot select more than ' + maxEntities.toString() + ' entities at a time!')
            } else {
                let singleMode = false
                let checkChildren = true
                let checkParents = true
                const settings = { singleMode, checkChildren, checkParents }
                // restore items in event when top most element was clicked and filter was set
                if (event.item.value.startsWith('0') && dataState.filter !== '')
                    event.item.items = dataState.nodes[0].items

                if (dataState.filter !== '') {
                    // Convert itemHierarchialIndex to dataState.nodes
                    let indices = event.itemHierarchicalIndex.split('_')
                    if (indices.length > 1) {
                        // Switch Entity index to dataState.nodes
                        // Find Entity index of event.value in unfiltered data
                        let entityIndex = -1
                        for (let i in dataState.nodes[0].items) {
                            if (event.item.value === dataState.nodes[0].items[i].value) {
                                entityIndex = i
                                break
                            }
                            // now check the sites
                            for (let j in dataState.nodes[0].items[i].items) {
                                if (
                                    event.item.value === dataState.nodes[0].items[i].items[j].value
                                ) {
                                    entityIndex = i
                                    break
                                }
                            }
                        }
                        if (entityIndex !== -1) {
                            // Replace entity in event's itemHierarchicalIndex
                            indices[1] = entityIndex
                            event.itemHierarchicalIndex = indices.join('_')
                        }
                    }
                }

                let newCheck = handleTreeViewCheckChange(
                    event,
                    dataState.check,
                    dataState.nodes,
                    settings
                )
                dispatch(custTreeViewCheckChanged(newCheck))
                onCustTVCheck(newCheck)

                // Expand entity level if not already expanded.
                if (event.item.value.startsWith('1')) {
                    let entityExpanded = false
                    for (var i in dataState.expand.ids) {
                        if (dataState.expand.ids[i] === event.item.text) entityExpanded = true
                    }
                    if (!entityExpanded) {
                        // rewrite required after going to new redux style of coding
                        //let newIds = dataState.expand.ids;
                        let newIds = JSON.parse(JSON.stringify(dataState.expand.ids))
                        newIds.push(event.item.text)
                        // need to add this since we are no longer allow to mutate contents of store directly
                        dispatch(custTreeViewExpandChanged({ ids: newIds, idField: 'text' }))
                    }
                }
            }
            if (event.item.value.startsWith('0') && dataState.filter !== '')
                dispatch(custTreeViewFilterChanged(''))
            // }
        }
    }

    function handleOnSearchChange(e) {
        dispatch(custTreeViewFilterChanged(e.target.value))
    }

    function getData() {
        let nodes = []
        nodes.push({
            expanded: dataState.nodes[0].expanded,
            text: dataState.nodes[0].text,
            value: dataState.nodes[0].value,
        })
        let nodeItems = dataState.nodes[0].items
        if (dataState.filter !== '') {
            nodeItems = []
            for (var i in dataState.nodes[0].items) {
                if (
                    dataState.nodes[0].items[i].text
                        .toUpperCase()
                        .includes(dataState.filter.toUpperCase())
                ) {
                    nodeItems.push(dataState.nodes[0].items[i])
                }
            }
            nodes[0].items = nodeItems
        } else {
            nodes[0].items = dataState.nodes[0].items
        }

        let data = processTreeViewItems(nodes, {
            check: dataState.check,
            expand: dataState.expand,
        })
        return data
    }
    return (
        <>
            <div style={{ paddingLeft: '8px' }}>
                {dataState.entityCount > 50 && (
                    <Input
                        type="search"
                        value={dataState.filter}
                        placeholder="Entity Search"
                        onChange={handleOnSearchChange}
                    />
                )}
                <TreeView
                    data={getData()}
                    idField={'value'}
                    expandIcons={true}
                    checkboxes={true}
                    onItemClick={onCheckChange}
                    onCheckChange={onCheckChange}
                    onExpandChange={onExpandChange}
                    aria-multiselectable={true}
                />
            </div>
        </>
    )
}
